'use client'

import { Checkbox, Chip, RadioButton, RadioGroup, TextField } from '@graftcode/design-system'

import { WIZARD_COPY } from '../../const'
import type { MethodScope, MethodsSectionProps } from '../../types'

import styles from './MethodsSection.module.scss'

export function MethodsSection({
  methodScope,
  graftableMethodsOnly,
  effectiveSelectedTypes,
  classesByFqn,
  perTypeMethods,
  targetType,
  activeMethods,
  constructorToken,
  selectedMethodNames,
  targetMethodSelection,
  methodPatternsText,
  isMemberGraftable,
  onMethodScopeChange,
  onTargetTypeChange,
  onToggleAllScopeMethod,
  onTogglePerTypeMethod,
  onMethodPatternsChange,
}: MethodsSectionProps) {
  const { methods: copy, sections } = WIZARD_COPY

  return (
    <section className={styles.section} aria-labelledby="graft-filter-wizard-methods">
      <h3 id="graft-filter-wizard-methods" className={styles.sectionTitle}>
        {sections.methods}
      </h3>

      <RadioGroup
        className={styles.scopeGroup}
        orientation="horizontal"
        value={methodScope}
        disabled={graftableMethodsOnly}
        onChange={(value) => onMethodScopeChange(value as MethodScope)}
      >
        <RadioButton value="all" label={copy.scopeAll} disabled={graftableMethodsOnly} />
        <RadioButton value="perType" label={copy.scopePerType} disabled={graftableMethodsOnly} />
      </RadioGroup>

      {methodScope === 'perType' && !graftableMethodsOnly && (
        <div className={styles.targetChips}>
          {effectiveSelectedTypes.length === 0 && <span className={styles.emptyHint}>{copy.selectTypeHint}</span>}
          {effectiveSelectedTypes.map((fqn) => (
            <Chip
              key={fqn}
              text={classesByFqn.get(fqn)?.name ?? fqn}
              active={targetType === fqn}
              badge={perTypeMethods[fqn]?.length || undefined}
              onClick={() => onTargetTypeChange(fqn)}
            />
          ))}
        </div>
      )}

      {graftableMethodsOnly ? (
        <span className={styles.emptyHint}>{copy.autoSelectedHint}</span>
      ) : activeMethods.names.length > 0 ? (
        <div className={styles.methodList}>
          {activeMethods.names.map((name) => {
            const isConstructor = name === constructorToken
            const graftable = isMemberGraftable(name)
            const checked =
              methodScope === 'all' ? selectedMethodNames.includes(name) : targetMethodSelection.includes(name)

            return (
              <label key={name} className={styles.methodOption}>
                <Checkbox
                  checked={checked}
                  disabled={methodScope === 'perType' && !targetType}
                  onChange={(value) =>
                    methodScope === 'all'
                      ? onToggleAllScopeMethod(name, value === true)
                      : onTogglePerTypeMethod(name, value === true)
                  }
                />
                <span className={styles.methodLabel}>
                  <code>{name}</code>
                  {isConstructor && <span className={styles.methodTag}>{copy.constructorTag}</span>}
                  {!graftable && <span className={styles.methodWarning}>{copy.nonGraftableTag}</span>}
                </span>
              </label>
            )
          })}
        </div>
      ) : (
        <span className={styles.emptyHint}>{copy.emptyHint}</span>
      )}

      <TextField
        label={copy.patternsLabel}
        placeholder={copy.patternsPlaceholder}
        helperText={copy.patternsHelper}
        value={methodPatternsText}
        disabled={graftableMethodsOnly}
        onChange={(event) => onMethodPatternsChange(event.target.value)}
      />
    </section>
  )
}
